import React, { useState, useEffect, useCallback, useRef } from 'react';
import { reportsAPI, departmentsAPI, categoriesAPI } from '../services/api';
import { searchCustomers, searchBooks } from '../utils/searchHelpers';
import { formatDateInIsrael } from '../utils/dateUtils';

function ReportsPage() {
  const [reports, setReports] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [departments, setDepartments] = useState([]);
  const [categories, setCategories] = useState([]);
  const [filters, setFilters] = useState({
    type: '',
    startDate: new Date(new Date().getFullYear(), new Date().getMonth() - 1, 1).toISOString().split('T')[0],
    endDate: new Date().toISOString().split('T')[0],
    customerId: '',
    bookId: '',
    departmentId: '',
    categoryId: ''
  });
  
  // חיפוש דינמי
  const [customerSearch, setCustomerSearch] = useState('');
  const [bookSearch, setBookSearch] = useState('');
  const [customerSearchResults, setCustomerSearchResults] = useState([]);
  const [bookSearchResults, setBookSearchResults] = useState([]);
  const [showCustomerDropdown, setShowCustomerDropdown] = useState(false);
  const [showBookDropdown, setShowBookDropdown] = useState(false);
  const filterTimeoutRef = useRef(null);
  
  useEffect(() => {
    loadStatistics();
    loadDepartmentsAndCategories();
  }, []);

  const loadDepartmentsAndCategories = async () => {
    try {
      const [deptsResponse, catsResponse] = await Promise.all([
        departmentsAPI.getAll(),
        categoriesAPI.getAll()
      ]);
      setDepartments(deptsResponse.data || []);
      setCategories(catsResponse.data || []);
    } catch (error) {
      console.error('Error loading departments/categories:', error);
    }
  };

  const loadReports = useCallback(async (filtersToUse) => {
    try {
      setLoading(true);
      console.log('Loading reports with filters:', filtersToUse);
      const response = await reportsAPI.getReports(filtersToUse);
      console.log('Reports response:', response.data);
      setReports(response.data || []);
    } catch (error) {
      console.error('Error loading reports:', error);
      setReports([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // Load reports when filters change with debounce to prevent rapid reloads
  useEffect(() => {
    if (filterTimeoutRef.current) {
      clearTimeout(filterTimeoutRef.current);
    }
    
    filterTimeoutRef.current = setTimeout(() => {
      loadReports(filters);
    }, 500); // Wait 500ms after filter change before loading

    return () => {
      if (filterTimeoutRef.current) {
        clearTimeout(filterTimeoutRef.current);
      }
    };
  }, [filters, loadReports]);

  const loadStatistics = async () => {
    try {
      const response = await reportsAPI.getStatistics();
      setStats(response.data);
    } catch (error) {
      console.error('Error loading statistics:', error);
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSearchCustomer = async (term) => {
    setCustomerSearch(term);
    if (term.length > 0) {
      const results = await searchCustomers(term);
      setCustomerSearchResults(results);
      setShowCustomerDropdown(true);
    } else {
      setCustomerSearchResults([]);
      setShowCustomerDropdown(false);
    }
  };

  const handleSearchBook = async (term) => {
    setBookSearch(term);
    if (term.length > 0) {
      console.log('Searching for books with term:', term);
      const results = await searchBooks(term, false);
      console.log('Book search results:', results);
      setBookSearchResults(results);
      setShowBookDropdown(true);
    } else {
      setBookSearchResults([]);
      setShowBookDropdown(false);
    }
  };

  const handleSelectCustomer = (customer) => {
    if (!customer || !customer.identity_id) return;
    setFilters(prev => ({
      ...prev,
      customerId: customer.identity_id.toString()
    }));
    setCustomerSearch(customer.name);
    setShowCustomerDropdown(false);
  };

  const handleSelectBook = (book) => {
    if (!book || !book.id) return;
    console.log('Selected book:', book);
    setFilters(prev => ({
      ...prev,
      bookId: book.id.toString()
    }));
    setBookSearch(book.book_name || book.title);
    setShowBookDropdown(false);
  };

  const clearCustomerFilter = () => {
    setFilters(prev => ({
      ...prev,
      customerId: ''
    }));
    setCustomerSearch('');
    setCustomerSearchResults([]);
    setShowCustomerDropdown(false);
  };

  const clearBookFilter = () => {
    setFilters(prev => ({
      ...prev,
      bookId: ''
    }));
    setBookSearch('');
    setBookSearchResults([]);
    setShowBookDropdown(false);
  };

  const handleExport = async () => {
    try {
      const response = await reportsAPI.export(filters);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `reports_${new Date().toISOString().split('T')[0]}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
    } catch (error) {
      console.error('Error exporting reports:', error);
      alert('שגיאה בייצוא הנתונים');
    }
  };

  const getStatusBadge = (status) => {
    return status === 'borrowed'
      ? '⏳ השאלה פעילה'
      : status === 'returned-loan'
        ? '📚 הושאל'
        : status === 'returned-return'
          ? '✓ הוחזר'
          : status;
  };

  if (loading && !stats) {
    return <div className="loading">טוען דוחות...</div>;
  }

  return (
    <div className="reports-page">
      <h1 className="page-title">📊 דוחות</h1>

      {stats && (
        <div className="stats-container" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '20px', marginBottom: '30px' }}>
          <div className="stat-card" style={{ background: 'white', padding: '20px', borderRadius: '8px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
            <div style={{ color: '#667eea', fontSize: '2rem', fontWeight: 'bold' }}>{stats.totalBooks}</div>
            <div style={{ color: '#999', marginTop: '10px' }}>סה"כ ספרים</div>
          </div>
          <div className="stat-card" style={{ background: 'white', padding: '20px', borderRadius: '8px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
            <div style={{ color: '#48bb78', fontSize: '2rem', fontWeight: 'bold' }}>{stats.totalCustomers}</div>
            <div style={{ color: '#999', marginTop: '10px' }}>סה"כ לקוחות</div>
          </div>
          <div className="stat-card" style={{ background: 'white', padding: '20px', borderRadius: '8px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
            <div style={{ color: '#f56565', fontSize: '2rem', fontWeight: 'bold' }}>{stats.borrowedBooks}</div>
            <div style={{ color: '#999', marginTop: '10px' }}>ספרים מושאלים</div>
          </div>
          <div className="stat-card" style={{ background: 'white', padding: '20px', borderRadius: '8px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
            <div style={{ color: '#ed8936', fontSize: '2rem', fontWeight: 'bold' }}>{stats.totalLoans}</div>
            <div style={{ color: '#999', marginTop: '10px' }}>סה"כ השאלות</div>
          </div>
          <div className="stat-card" style={{ background: 'white', padding: '20px', borderRadius: '8px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
            <div style={{ color: '#9f7aea', fontSize: '2rem', fontWeight: 'bold' }}>{stats.activeSubscriptions}</div>
            <div style={{ color: '#999', marginTop: '10px' }}>מנויים פעילים</div>
          </div>
        </div>
      )}

      <div style={{ background: 'white', padding: '20px', borderRadius: '8px', marginBottom: '20px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
        <h3 style={{ marginBottom: '15px' }}>סנון דוחות</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '15px', marginBottom: '15px' }}>
          <div className="form-group">
            <label>סוג:</label>
            <select value={filters.type} onChange={(e) => handleFilterChange('type', e.target.value)}>
              <option value="">הכל</option>
              <option value="borrowed">השאלות פעילות</option>
              <option value="returned-loan">השאלות</option>
              <option value="returned-return">החזרות</option>
            </select>
          </div>
          <div className="form-group">
            <label>מתאריך:</label>
            <input
              type="date"
              value={filters.startDate}
              onChange={(e) => handleFilterChange('startDate', e.target.value)}
            />
          </div>
          <div className="form-group">
            <label>עד תאריך:</label>
            <input
              type="date"
              value={filters.endDate}
              onChange={(e) => handleFilterChange('endDate', e.target.value)}
            />
          </div>
          <div className="form-group">
            <label>מחלקה:</label>
            <select value={filters.departmentId} onChange={(e) => handleFilterChange('departmentId', e.target.value)}>
              <option value="">הכל</option>
              {departments.map(dept => (
                <option key={dept.id} value={dept.id}>{dept.name}</option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label>קטגוריה:</label>
            <select value={filters.categoryId} onChange={(e) => handleFilterChange('categoryId', e.target.value)}>
              <option value="">הכל</option>
              {categories.map(cat => (
                <option key={cat.id} value={cat.id}>{cat.name}</option>
              ))}
            </select>
          </div>
          <div className="form-group" style={{ position: 'relative' }}>
            <label>לקוח:</label>
            <div style={{ display: 'flex', gap: '5px', alignItems: 'center' }}>
              <input
                type="text"
                value={customerSearch}
                onChange={(e) => handleSearchCustomer(e.target.value)}
                onFocus={() => customerSearch && setShowCustomerDropdown(true)}
                placeholder="חפש לקוח..."
                style={{ flex: 1 }}
              />
              {filters.customerId && (
                <button 
                  className="btn btn-danger btn-sm"
                  onClick={clearCustomerFilter}
                  style={{ padding: '5px 10px' }}
                >
                  ✕
                </button>
              )}
            </div>
            {showCustomerDropdown && customerSearchResults.length > 0 && (
              <div className="search-results-dropdown">
                {customerSearchResults.map((customer) => (
                  <div
                    key={customer.id}
                    className="search-result-item"
                    onClick={() => handleSelectCustomer(customer)}
                  >
                    <div><strong>{customer.name}</strong></div>
                    <div style={{ fontSize: '0.9rem', color: '#666' }}>
                      {customer.phone && <span>📞 {customer.phone}</span>}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          <div className="form-group" style={{ position: 'relative' }}>
            <label>ספר:</label>
            <div style={{ display: 'flex', gap: '5px', alignItems: 'center' }}>
              <input
                type="text"
                value={bookSearch}
                onChange={(e) => handleSearchBook(e.target.value)}
                onFocus={() => bookSearch && setShowBookDropdown(true)}
                placeholder="חפש ספר..."
                style={{ flex: 1 }}
              />
              {filters.bookId && (
                <button 
                  className="btn btn-danger btn-sm"
                  onClick={clearBookFilter}
                  style={{ padding: '5px 10px' }}
                >
                  ✕
                </button>
              )}
            </div>
            {showBookDropdown && bookSearchResults.length > 0 && (
              <div className="search-results-dropdown">
                {bookSearchResults.map((book) => (
                  <div
                    key={book.id}
                    className="search-result-item"
                    onClick={() => handleSelectBook(book)}
                  >
                    <div><strong>{book.book_name}</strong></div>
                    <div style={{ fontSize: '0.9rem', color: '#666' }}>
                      {book.author}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
        <div style={{ display: 'flex', gap: '10px' }}>
          <button className="btn btn-warning" onClick={handleExport}>
            📊 ייצוא לאקסל
          </button>
        </div>
      </div>

      {reports.length === 0 ? (
        <div className="empty-state">
          <h3>אין דוחות</h3>
          <p>לא נמצאו רשומות התואמות לסנונים שלך</p>
        </div>
      ) : (
        <table className="data-table">
          <thead>
            <tr>
              <th>תאריך</th>
              <th>שם ספר</th>
              <th>שם לקוח</th>
              <th>מזהה לקוח</th>
              <th>מצב הספר</th>
              <th>סטטוס</th>
              <th>הערות</th>
            </tr>
          </thead>
          <tbody>
            {reports.map((report, index) => (
              <tr key={index}>
                <td>{formatDateInIsrael(report.event_date || report.loan_date)}</td>
                <td>{report.book_title}</td>
                <td>{report.customer_name}</td>
                <td>{report.identity_id}</td>
                <td>{report.condition_at_time || '-'}</td>
                <td>
                  <span className={`badge badge-${report.status === 'returned-loan' ? 'returned' : report.status === 'returned-return' ? 'returned' : report.status}`}>
                    {getStatusBadge(report.status)}
                  </span>
                </td>
                <td>{report.notes || '-'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

export default ReportsPage;
